// Files admins can upload as Trust Center resources.
// Keep in sync with regodit-api middlewares/uploadTrustCenterDocument.js, which
// enforces the same rules on the server.
export const ALLOWED_EXTENSIONS = [
  ".ai",
  ".csv",
  ".docx",
  ".jpg",
  ".jpeg",
  ".json",
  ".pdf",
  ".png",
  ".txt",
  ".webp",
  ".xlsx",
];

export const MAX_UPLOAD_MB = 75;

export const ACCEPT_ATTRIBUTE = ALLOWED_EXTENSIONS.join(",");

export const UPLOAD_HINT = `Up to ${MAX_UPLOAD_MB} MB. Allowed: ${ALLOWED_EXTENSIONS.join(", ")}`;

const extensionOf = (fileName) => {
  const match = /\.[^.]+$/.exec(fileName || "");
  return match ? match[0].toLowerCase() : "";
};

// Returns a validator: null when the file can be uploaded, or a message.
const makeValidator = (extensions, maxMb, hint) => (file) => {
  if (!file) return "Choose a file to upload.";
  const extension = extensionOf(file.name);
  if (!extensions.includes(extension)) {
    const what = extension
      ? `${extension} files are`
      : "Files without an extension are";
    return `${what} not allowed. ${hint}.`;
  }
  if (file.size > maxMb * 1024 * 1024) {
    const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
    return `This file is ${sizeMb} MB. The limit is ${maxMb} MB.`;
  }
  return null;
};

export const validateUpload = makeValidator(
  ALLOWED_EXTENSIONS,
  MAX_UPLOAD_MB,
  UPLOAD_HINT
);

// Photos and drawings. ".jpeg" and ".jpg" are the same format; both names
// are common.
const RASTER_EXTENSIONS = [".png", ".jpg", ".jpeg", ".webp"];

// Company logo, shown in the public page header. An SVG is turned into a PNG
// by the server (an SVG can carry scripts).
export const LOGO_EXTENSIONS = [...RASTER_EXTENSIONS, ".svg"];
export const MAX_LOGO_MB = 2;
export const LOGO_ACCEPT_ATTRIBUTE = LOGO_EXTENSIONS.join(",");
export const LOGO_HINT = `PNG, JPG, WEBP or SVG, up to ${MAX_LOGO_MB} MB. A wide logo on a clear background works best`;
export const validateLogo = makeValidator(
  LOGO_EXTENSIONS,
  MAX_LOGO_MB,
  LOGO_HINT
);

// Hero (banner) image behind the public page's hero. Keep in sync with
// regodit-api middlewares/uploadHeroImage.js.
export const MAX_HERO_MB = 5;
export const HERO_HINT = `PNG, JPG or WEBP, up to ${MAX_HERO_MB} MB. A wide image (about 2400 × 900) works best; text sits on top of it`;
export const HERO_ACCEPT_ATTRIBUTE = RASTER_EXTENSIONS.join(",");
export const validateHeroImage = makeValidator(
  RASTER_EXTENSIONS,
  MAX_HERO_MB,
  HERO_HINT
);

// Browser-tab icon: small and square.
export const MAX_FAVICON_MB = 0.5;
export const FAVICON_EXTENSIONS = [".ico", ...LOGO_EXTENSIONS];
export const FAVICON_ACCEPT_ATTRIBUTE = FAVICON_EXTENSIONS.join(",");
export const FAVICON_HINT =
  "ICO, PNG, JPG, WEBP or SVG, up to 512 KB. A square image (at least 64 × 64) works best";
export const validateFavicon = makeValidator(
  FAVICON_EXTENSIONS,
  MAX_FAVICON_MB,
  FAVICON_HINT
);

// One file of a custom font (one per weight). Keep in sync with regodit-api
// middlewares/uploadFontFile.js.
export const FONT_EXTENSIONS = [".woff2", ".woff", ".ttf", ".otf"];
export const MAX_FONT_MB = 2;
export const FONT_ACCEPT_ATTRIBUTE = FONT_EXTENSIONS.join(",");
export const FONT_HINT = `WOFF2, WOFF, TTF or OTF, up to ${MAX_FONT_MB} MB each`;
export const validateFont = makeValidator(
  FONT_EXTENSIONS,
  MAX_FONT_MB,
  FONT_HINT
);
